import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { isInEnum } from '@thesavingsgroup/utils/isInEnum';
import i18n from 'i18next';
import { StateCreator } from 'zustand';

import { isValidVin } from '../applyStore/functions/isValidVin';
import { SignUpState } from './types/SignUpState';
import { SignUpStateDependencies } from './types/SignUpStateDependencies';

export const signUpStore =
  ({ useParameterStore }: SignUpStateDependencies): StateCreator<SignUpState> =>
  () => ({
    firstName: '',
    lastName: '',
    primaryEmailAddress: '',
    primaryPhoneNumber: '',

    computed: {
      get applyRoute(): string {
        const { applicationType, vehicle_vin } = useParameterStore.getState();

        if (applicationType === ApplicationType.PURCHASE) {
          // we do not support collection of purchase type via parameters, so we route
          // purchase sign up to the purchase type selection page.
          return '/apply/purchase-type';
        }

        if (isValidVin(vehicle_vin)) {
          // If there's a valid VIN, we can skip to confirming vehicle details.
          return '/apply/confirm-vehicle';
        } else if (applicationType === ApplicationType.REFINANCE) {
          // when there's an invalid VIN for a refinance sign up, we route to the refinance goal page.
          return '/apply/refinance-goal';
        } else if (applicationType === ApplicationType.LEASE_BUYOUT) {
          // when there's an invalid VIN for a lease buyout sign up, we route to the search vehicle page.
          return '/apply/search-vehicle';
        }

        // In any other case route to the vehicle selection page.
        return '/apply/vehicle-selection';
      },
      get signUpSubmitButtonText(): string {
        const { applicationType } = useParameterStore.getState();

        const isDefaultButtonText = !isInEnum(applicationType, ApplicationType);

        return isDefaultButtonText
          ? i18n.t('SignUpPage.submitButton.default')
          : i18n.t(`SignUpPage.submitButton.${applicationType}`);
      },
      get signUpSubtitleFirstLineText(): string {
        const { applicationType } = useParameterStore.getState();

        const isDefaultSubtitle = !isInEnum(applicationType, ApplicationType);

        return isDefaultSubtitle
          ? i18n.t('SignUpPage.subtitle.default.firstLine')
          : i18n.t(`SignUpPage.subtitle.${applicationType}.firstLine`);
      },
      get signUpSubtitleSecondLineText(): string {
        const { applicationType } = useParameterStore.getState();

        const isDefaultSubtitle = !isInEnum(applicationType, ApplicationType);

        return isDefaultSubtitle
          ? i18n.t('SignUpPage.subtitle.default.secondLine')
          : i18n.t(`SignUpPage.subtitle.${applicationType}.secondLine`);
      },
      get signUpSubtitleThirdLineText(): string {
        const { applicationType } = useParameterStore.getState();

        const isDefaultSubtitle = !isInEnum(applicationType, ApplicationType);

        return isDefaultSubtitle
          ? i18n.t('SignUpPage.subtitle.default.thirdLine')
          : i18n.t(`SignUpPage.subtitle.${applicationType}.thirdLine`);
      },
      get signUpTitleText(): string {
        const { applicationType } = useParameterStore.getState();

        const isDefaultTitle = !isInEnum(applicationType, ApplicationType);

        return isDefaultTitle
          ? i18n.t('SignUpPage.title.default')
          : i18n.t(`SignUpPage.title.${applicationType}`);
      },
    },
  });
